import { ArrowUpLeft, Check, Copy, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { MessageTextStyle } from '@/entities/hey-syncly';
import { StudioItemMarkdown } from '@/features/Studio/components/StudioItemDetail/studio-item-markdown';
import { Badge, Button, Input, Tooltip } from '@/shared/ui';
import { clipboardCopy } from '@/utils/util';

import { PromptLibraryItemRow } from './saved-prompts-item-row';
import type { PromptLibraryItem } from './types';

type Props = {
  isLoading: boolean;
  items: PromptLibraryItem[];
  onDeletePrompt: (item: PromptLibraryItem) => void;
  onEditPrompt: (item: PromptLibraryItem) => void;
  onSelectPrompt: (item: PromptLibraryItem) => void;
  close: () => void;
  searchQuery: string;
  selectedPromptId: string | null;
  onSearchQueryChange: (value: string) => void;
  onSelectedPromptChange: (promptId: string) => void;
};

export default function PromptLibraryDialogBody({
  isLoading,
  items,
  onDeletePrompt,
  onEditPrompt,
  onSelectPrompt,
  close,
  searchQuery,
  selectedPromptId,
  onSearchQueryChange,
  onSelectedPromptChange,
}: Props) {
  const filteredPrompts = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();

    if (!normalizedSearch) {
      return items;
    }

    return items.filter((item) => {
      return (
        item.title.toLowerCase().includes(normalizedSearch) ||
        item.prompt.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [items, searchQuery]);

  const hasPrompts = filteredPrompts.length > 0;
  const selectedItem = hasPrompts
    ? (filteredPrompts.find((item) => item.id === selectedPromptId) ??
      filteredPrompts[0])
    : null;
  const selectedItemId = selectedItem?.id ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 w-[45%] shrink-0 flex-col border-r border-border">
          <div className="relative shrink-0 px-4 py-4">
            <Search className="absolute top-1/2 left-7 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              onChange={(event) => {
                onSearchQueryChange(event.target.value);
              }}
              placeholder="Search prompts..."
              type="text"
              value={searchQuery}
              className="pl-8 h-9"
              autoFocus
            />
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto px-4 pb-4"
            style={{ scrollbarGutter: 'stable', scrollbarWidth: 'thin' }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Loading saved prompts...
              </div>
            ) : filteredPrompts.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                {searchQuery.trim()
                  ? 'No prompts found'
                  : 'No saved prompts yet'}
              </div>
            ) : (
              filteredPrompts.map((item) => (
                <PromptLibraryItemRow
                  key={item.id}
                  item={item}
                  onClick={() => onSelectedPromptChange(item.id)}
                  onDelete={onDeletePrompt}
                  onEdit={onEditPrompt}
                  selected={selectedItemId === item.id}
                  showActions={false}
                />
              ))
            )}
          </div>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {selectedItem !== null ? (
            <SelectedItemBody key={selectedItem.id} item={selectedItem} />
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              {isLoading
                ? 'Loading saved prompts...'
                : 'Select a prompt to preview'}
            </div>
          )}
        </div>
      </div>
      <div className="shrink-0 border-t border-border px-7 py-4">
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={
              selectedItem ? () => onSelectPrompt(selectedItem) : undefined
            }
            disabled={selectedItem === null}
          >
            <ArrowUpLeft className="size-4" />
            Use this prompt
          </Button>
        </div>
      </div>
    </div>
  );
}

type BodyProps = {
  item: PromptLibraryItem;
};
const SelectedItemBody = ({ item }: BodyProps) => {
  const [copied, setCopied] = useState(false);

  const copyPromptToClipboard = () => {
    setCopied(true);
    clipboardCopy(item.prompt);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="min-h-0 flex-1 overflow-y-auto px-7 py-5"
        style={{
          scrollbarGutter: 'stable',
          scrollbarWidth: 'thin',
        }}
      >
        <div className="mb-4 flex min-h-9 items-start justify-between gap-3">
          <h3 className="text-lg font-semibold text-foreground">
            <span className="flex items-center gap-2">
              <span>{item.title}</span>
              {item.is_default && <Badge variant="secondary">Template</Badge>}
            </span>
          </h3>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center">
            <span className="text-sm font-medium text-foreground">Prompt</span>
            <Tooltip content="Copy prompt to clipboard" disabled={copied}>
              <span>
                <Button
                  onClick={copyPromptToClipboard}
                  size="icon-xs"
                  variant="ghost"
                  disabled={copied}
                >
                  {copied ? (
                    <Check className="size-3.5 text-green-700" />
                  ) : (
                    <Copy className="size-3.5" />
                  )}
                </Button>
              </span>
            </Tooltip>
          </div>

          <MessageTextStyle
            className="[&_h1]:!mt-4 [&_h1]:!text-3xl rounded-lg bg-secondary/50 p-4"
            style={{ ['--font-size' as string]: '14px' }}
          >
            <StudioItemMarkdown content={item.prompt} />
          </MessageTextStyle>
        </div>
      </div>
    </div>
  );
};
